import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiJson } from '@/lib/api';
import type {
  AdminCoachEngagementRecentSendRow,
  AdminCoachEngagementStatusResponse,
} from '@/lib/types';

const STATUS_QUERY_KEY = ['admin', 'coach-engagement', 'status'] as const;
const RECENT_SENDS_QUERY_KEY = ['admin', 'coach-engagement', 'recent-sends'] as const;

function formatRelativeSeconds(totalSeconds: number): string {
  if (totalSeconds <= 0) return 'now';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${remMinutes}m`;
  }
  if (minutes >= 1) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatEmailKey(key: string): string {
  return key
    .replace(/^pro_day_/, 'Day ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match: string) => match.toUpperCase());
}

function AdminCoachEngagementPage() {
  const { getToken } = useAuth();

  const statusQuery = useQuery({
    queryKey: STATUS_QUERY_KEY,
    queryFn: () =>
      apiJson<AdminCoachEngagementStatusResponse>(
        getToken,
        '/v1/admin/coach-engagement/status',
      ),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const recentSendsQuery = useQuery({
    queryKey: RECENT_SENDS_QUERY_KEY,
    queryFn: () =>
      apiJson<AdminCoachEngagementRecentSendRow[]>(
        getToken,
        '/v1/admin/coach-engagement/recent-sends?limit=100',
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const serverNextRunSeconds = statusQuery.data?.next_run_in_seconds ?? null;
  const [secondsUntilNextRun, setSecondsUntilNextRun] = useState<number | null>(
    serverNextRunSeconds,
  );

  useEffect(() => {
    setSecondsUntilNextRun(serverNextRunSeconds);
  }, [serverNextRunSeconds]);

  useEffect(() => {
    if (serverNextRunSeconds === null) return;
    const id = window.setInterval(() => {
      setSecondsUntilNextRun((prev) =>
        prev === null ? null : Math.max(0, prev - 1),
      );
    }, 1000);
    return () => window.clearInterval(id);
  }, [serverNextRunSeconds]);

  const status = statusQuery.data;

  return (
    <div className='space-y-8'>
      <div className='flex flex-wrap items-center gap-3'>
        <button
          type='button'
          onClick={() => {
            void statusQuery.refetch();
            void recentSendsQuery.refetch();
          }}
          className='px-3 py-2 rounded-md border border-nova-input text-sm font-semibold text-nova-foreground hover:bg-nova-accent'
        >
          Refresh
        </button>
        {statusQuery.isFetching ? (
          <span className='text-xs text-nova-muted-foreground'>Updating…</span>
        ) : null}
      </div>

      {statusQuery.isLoading ? (
        <div className='flex justify-center py-16'>
          <div className='animate-spin h-10 w-10 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : statusQuery.isError ? (
        <p className='text-red-600 text-sm'>Could not load coach engagement status.</p>
      ) : status ? (
        <>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
            <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
                Next run sends
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-nova-foreground'>
                {status.next_run_will_send_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-nova-muted-foreground'>
                Professionals expected to receive an email next beat run
              </p>
            </div>
            <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
                Next Celery run
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
                {status.schedule_environment === 'production' &&
                secondsUntilNextRun !== null
                  ? `in ${formatRelativeSeconds(secondsUntilNextRun)}`
                  : status.schedule_description}
              </p>
              <p className='mt-1 text-xs text-nova-muted-foreground'>
                {status.next_run_at_utc
                  ? `${new Date(status.next_run_at_utc).toLocaleString()} (local) · ${status.schedule_description}`
                  : status.schedule_description}
              </p>
            </div>
            <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
                Professional accounts
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-nova-foreground'>
                {status.professionals_total.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-nova-muted-foreground'>
                {status.professionals_actionable.toLocaleString()} currently actionable
              </p>
            </div>
            <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
                Send health
              </p>
              <p className='mt-1 text-sm font-semibold text-nova-foreground'>
                {status.email_suppressed_user_count.toLocaleString()} suppressed ·{' '}
                {status.unsubscribed_global_count.toLocaleString()} global unsub ·{' '}
                {status.unsubscribed_campaign_count.toLocaleString()} campaign unsub
              </p>
              <p className='mt-1 text-xs text-nova-muted-foreground'>
                Suppression and unsubscribe safety rails
              </p>
            </div>
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2'>
            {(
              [
                ['Sends (7d)', status.sends_last_7_days],
                ['Sends (30d)', status.sends_last_30_days],
                ['Sends (all)', status.sends_all_time],
                ['Invalid recipient skips', status.skipped_invalid_recipient_all_time],
                ['Not queued', status.pending_not_queued.length],
              ] as const
            ).map(([label, value]) => (
              <div
                key={label}
                className='bg-nova-card rounded-lg border border-nova-border px-3 py-3 shadow-sm'
              >
                <p className='text-[10px] font-semibold uppercase tracking-wide text-nova-muted-foreground leading-tight'>
                  {label}
                </p>
                <p className='mt-1 text-lg font-semibold tabular-nums text-nova-foreground'>
                  {value.toLocaleString()}
                </p>
              </div>
            ))}
          </div>

          <section className='bg-nova-card rounded-lg border border-nova-border shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-nova-border'>
              <h2 className='text-lg font-semibold text-nova-foreground'>Next run queue</h2>
              <p className='text-xs text-nova-muted-foreground mt-1'>
                Ordered candidates for the next send. Click a user to inspect
                account-level details.
              </p>
            </div>
            {!status.upcoming_sends.length ? (
              <p className='px-4 py-4 text-sm text-nova-muted-foreground'>
                No users currently queued for a pro education send.
              </p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead className='bg-nova-muted text-left text-xs font-semibold uppercase text-nova-muted-foreground'>
                    <tr>
                      <th className='px-4 py-2'>Professional</th>
                      <th className='px-4 py-2'>Next email</th>
                      <th className='px-4 py-2'>Days since start</th>
                      <th className='px-4 py-2'>Accepted clients</th>
                      <th className='px-4 py-2'>Available codes</th>
                      <th className='px-4 py-2'>Already sent</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-nova-border'>
                    {status.upcoming_sends.map((row) => (
                      <tr key={row.user_id}>
                        <td className='px-4 py-2 text-nova-foreground'>
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(row.user_id) }}
                            search={{ hid: undefined }}
                            className='font-medium text-nova-primary hover:underline'
                          >
                            {row.professional_email}
                          </Link>
                          <div className='text-xs text-nova-muted-foreground'>{row.professional_name ?? '—'}</div>
                        </td>
                        <td className='px-4 py-2 font-medium text-nova-primary'>
                          {formatEmailKey(row.next_email_key)}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-nova-foreground'>
                          {row.days_since_reference}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-nova-foreground'>
                          {row.accepted_client_count}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-nova-foreground'>
                          {row.available_code_count}
                        </td>
                        <td className='px-4 py-2 text-xs text-nova-muted-foreground max-w-sm'>
                          {row.sent_keys.length ? row.sent_keys.map(formatEmailKey).join(', ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className='bg-nova-card rounded-lg border border-amber-200 bg-amber-50/40 shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-amber-200/80'>
              <h2 className='text-lg font-semibold text-nova-foreground'>Not queued yet</h2>
              <p className='text-xs text-nova-muted-foreground mt-1'>
                Professionals excluded from the next run with a reason (e.g.,
                waiting for a delay, unsubscribed, recently emailed, or fully complete).
              </p>
            </div>
            {!status.pending_not_queued.length ? (
              <p className='px-4 py-4 text-sm text-nova-muted-foreground'>Everyone is queued.</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead className='bg-amber-50/80 text-left text-xs font-semibold uppercase text-nova-muted-foreground'>
                    <tr>
                      <th className='px-4 py-2'>Professional</th>
                      <th className='px-4 py-2'>Next step</th>
                      <th className='px-4 py-2'>Why not queued</th>
                      <th className='px-4 py-2'>Days since start</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-amber-100 bg-nova-card/60'>
                    {status.pending_not_queued.map((row) => (
                      <tr key={row.user_id}>
                        <td className='px-4 py-2 text-nova-foreground align-top'>
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(row.user_id) }}
                            search={{ hid: undefined }}
                            className='font-medium text-nova-primary hover:underline'
                          >
                            {row.professional_email}
                          </Link>
                        </td>
                        <td className='px-4 py-2 align-top whitespace-nowrap'>
                          {row.next_email_key ? formatEmailKey(row.next_email_key) : '—'}
                          {row.days_until_next_email != null && row.days_until_next_email > 0 ? (
                            <div className='text-xs text-amber-800'>
                              in ~{row.days_until_next_email}d
                            </div>
                          ) : null}
                        </td>
                        <td className='px-4 py-2 text-nova-foreground align-top max-w-lg'>
                          {row.reason}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-nova-foreground align-top'>
                          {row.days_since_reference}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className='bg-nova-card rounded-lg border border-nova-border shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-nova-border'>
              <h2 className='text-lg font-semibold text-nova-foreground'>Campaign cadence</h2>
            </div>
            <ul className='divide-y divide-nova-border text-sm'>
              {status.cadence.map((step) => (
                <li key={step.email_key} className='px-4 py-2'>
                  <span className='font-medium text-nova-foreground'>
                    {formatEmailKey(step.email_key)}
                  </span>
                  <span className='text-nova-muted-foreground'> (≥ {step.delay_days}d)</span>
                  <span className='text-nova-muted-foreground'> — {step.subject}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      ) : null}

      <section className='bg-nova-card rounded-lg border border-nova-border shadow-sm overflow-hidden'>
        <div className='px-4 py-3 border-b border-nova-border'>
          <h2 className='text-lg font-semibold text-nova-foreground'>Recent sends</h2>
          <p className='text-xs text-nova-muted-foreground mt-1'>
            Latest sends from drip log for campaign: <code>pro_education</code>.
          </p>
        </div>
        {recentSendsQuery.isLoading ? (
          <p className='px-4 py-4 text-sm text-nova-muted-foreground'>Loading…</p>
        ) : recentSendsQuery.isError ? (
          <p className='px-4 py-4 text-sm text-red-600'>Could not load sends.</p>
        ) : !recentSendsQuery.data?.length ? (
          <p className='px-4 py-4 text-sm text-nova-muted-foreground'>No sends logged yet.</p>
        ) : (
          <div className='overflow-x-auto'>
            <table className='min-w-full text-sm'>
              <thead className='bg-nova-muted text-left text-xs font-semibold uppercase text-nova-muted-foreground'>
                <tr>
                  <th className='px-4 py-2'>Sent</th>
                  <th className='px-4 py-2'>Key</th>
                  <th className='px-4 py-2'>Professional</th>
                  <th className='px-4 py-2'>Mailgun id</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-nova-border'>
                {recentSendsQuery.data.map((row) => (
                  <tr key={row.id}>
                    <td className='px-4 py-2 text-nova-muted-foreground whitespace-nowrap'>
                      {new Date(row.sent_at).toLocaleString()}
                    </td>
                    <td className='px-4 py-2 font-medium text-nova-foreground'>
                      {formatEmailKey(row.email_key)}
                    </td>
                    <td className='px-4 py-2 text-nova-foreground'>
                      <Link
                        to='/admin/users/$userId'
                        params={{ userId: String(row.user_id) }}
                        search={{ hid: undefined }}
                        className='text-nova-primary hover:underline'
                      >
                        {row.professional_email}
                      </Link>
                    </td>
                    <td className='px-4 py-2 text-xs text-nova-muted-foreground font-mono max-w-[16rem] truncate'>
                      {row.mailgun_message_id ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export const Route = createFileRoute('/admin/professionals/engagement')({
  component: AdminCoachEngagementPage,
});

